<template>
  <DashboardWidget
    title="Team Chat"
    icon="fa fa-users"
    :view-all-link="{ name: 'chat' }"
    :loading="loading"
    :is-empty="!hasRooms"
    empty-icon="fa fa-users"
    empty-title="No team chats"
    empty-text="Start a conversation with your team"
  >
    <div class="team-chat-summary">
      <!-- Unread Badge -->
      <div v-if="totalUnreadCount > 0" class="unread-banner">
        <i class="fa fa-comment"></i>
        <span>{{ totalUnreadCount }} unread message{{ totalUnreadCount !== 1 ? 's' : '' }}</span>
      </div>

      <!-- Recent Chats List -->
      <div class="chats-list">
        <div
          v-for="room in recentRooms"
          :key="room.roomId"
          class="list-item chat-item"
          :class="{ 'has-unread': getUnreadCount(room) > 0 }"
          @click="goToChat(room)"
        >
          <div class="item-avatar" :style="getAvatarStyle(room)">
            {{ getInitials(room) }}
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-title">{{ room.roomName }}</span>
              <span class="item-time">{{ getTimeAgo(room) }}</span>
            </div>
            <p class="item-preview">
              {{ getLastMessagePreview(room) }}
            </p>
          </div>
          <div v-if="getUnreadCount(room) > 0" class="unread-badge">
            {{ getUnreadCount(room) }}
          </div>
        </div>
      </div>

      <!-- Empty state when no recent activity -->
      <div v-if="hasRooms && recentRooms.length === 0" class="no-recent">
        <i class="fa fa-check-circle"></i>
        <span>All caught up!</span>
      </div>
    </div>
  </DashboardWidget>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import DashboardWidget from './DashboardWidget.vue';
import moment from 'moment-timezone';
import mixin from '../../../mixin.js';

export default {
  name: 'TeamChatWidget',

  components: {
    DashboardWidget
  },

  mixins: [mixin],

  props: {
    maxItems: {
      type: Number,
      default: 4
    }
  },

  data() {
    return {
      isLoadingChat: true
    };
  },

  computed: {
    ...mapState('chat', ['rooms', 'roomsLoaded', 'loadingRooms', 'currentUserId']),

    loading() {
      return this.isLoadingChat;
    },

    hasRooms() {
      return this.rooms && this.rooms.length > 0;
    },

    totalUnreadCount() {
      if (!this.rooms) return 0;
      return this.rooms.reduce((total, room) => {
        return total + (this.getUnreadCount(room) || 0);
      }, 0);
    },

    recentRooms() {
      if (!this.rooms) return [];
      // Sort by lastUpdated or lastMessage timestamp, take top N
      return [...this.rooms]
        .sort((a, b) => {
          const dateA = this.getRoomTimestamp(a);
          const dateB = this.getRoomTimestamp(b);
          return dateB - dateA;
        })
        .slice(0, this.maxItems);
    }
  },

  async mounted() {
    await this.loadChatRooms();
  },

  methods: {
    ...mapActions('chat', ['_fetchMoreRooms']),

    async loadChatRooms() {
      this.isLoadingChat = true;
      try {
        // Only fetch if not already loaded
        if (!this.roomsLoaded && this.userID) {
          await this._fetchMoreRooms({
            currentUserId: this.userID,
            roomsPerPage: 20,
            dateFormatStr: this.dateFormatStr || 'MM/DD/YY'
          });
        }
      } catch (error) {
        console.error('Error loading chat rooms:', error);
      } finally {
        this.isLoadingChat = false;
      }
    },

    goToChat(room) {
      this.$router.push({ name: 'chat', query: { room: room.roomId } });
    },

    getUnreadCount(room) {
      if (room.unreadCount !== undefined) {
        return parseInt(room.unreadCount) || 0;
      }
      if (room.unreadMap && this.userID) {
        return room.unreadMap[this.userID] || 0;
      }
      return 0;
    },

    getRoomTimestamp(room) {
      if (room.lastMessage && room.lastMessage.date) {
        return room.lastMessage.date * 1000;
      }
      if (room.lastUpdated) {
        if (room.lastUpdated.seconds) {
          return room.lastUpdated.seconds * 1000;
        }
        return new Date(room.lastUpdated).getTime();
      }
      return 0;
    },

    getTimeAgo(room) {
      const timestamp = this.getRoomTimestamp(room);
      if (!timestamp) return '';
      return moment(timestamp).fromNow();
    },

    getLastMessagePreview(room) {
      if (room.lastMessage && room.lastMessage.content) {
        const content = room.lastMessage.content;
        return content.length > 40 ? content.substring(0, 40) + '...' : content;
      }
      return 'No messages yet';
    },

    getInitials(room) {
      if (!room.roomName) return '?';
      const words = room.roomName.split(' ');
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return room.roomName.substring(0, 2).toUpperCase();
    },

    getAvatarStyle(room) {
      // Generate consistent color based on room name
      const colors = [
        '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b',
        '#10b981', '#06b6d4', '#6366f1', '#84cc16'
      ];
      const index = room.roomName ? room.roomName.charCodeAt(0) % colors.length : 0;
      return {
        backgroundColor: colors[index],
        color: 'white'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.team-chat-summary {
  .unread-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: var(--widget-stat-padding, 10px 14px);
    background-color: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    margin: 12px;
    color: #3b82f6;
    font-size: 0.875rem;
    font-weight: 500;

    i {
      font-size: 1rem;
    }
  }

  .chats-list {
    max-height: 280px;
    overflow-y: auto;
  }

  .chat-item {
    cursor: pointer;
    transition: background-color 0.15s ease;
    position: relative;
    padding-right: 40px;

    &:hover {
      background-color: #f8fafc;
    }

    &.has-unread {
      background-color: rgba(59, 130, 246, 0.03);

      .item-title {
        font-weight: 600;
      }
    }
  }

  .item-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    flex-shrink: 0;
  }

  .unread-badge {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 10px;
    background-color: #3b82f6;
    color: white;
    font-size: 0.6875rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .no-recent {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: #64748b;

    i {
      color: #22c55e;
      font-size: 1.25rem;
    }
  }
}
</style>
